import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_CSV,
  downloadCampaignCsv,
  toCampaignActivityGroup,
} from '#/entities/campaign'
import type { CampaignActivityGroup } from '#/entities/campaign'
import { useListActivityApiV1CampaignsCampaignIdActivityGet } from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'
import type { DataTablePagination } from '#/shared/ui/data-table'

import {
  CAMPAIGN_ACTIVITY_ALERT_FILTERS,
  CAMPAIGN_ACTIVITY_FILTERS,
  CAMPAIGN_ACTIVITY_FILTER_ORDER,
  campaignActivityFilterLabelKey,
} from './activity-filters'
import type { CampaignActivityFilter } from './activity-filters'
import type { CampaignChip } from './chips'

const PAGE_SIZE = 50

/**
 * The Activity tab's state. Only the slice is local — the feed is
 * `GET /campaigns/{id}/activity`, and the filter goes with the request rather
 * than trimming an already-fetched page. Every chip count comes back on every
 * request, so switching chips never blanks the strip.
 *
 * The feed is paged server-side, and the `UPCOMING` band is page 1's alone:
 * what has not happened yet belongs at the top of the history, not repeated
 * down the middle of it. So nothing here re-sorts or re-groups — the groups
 * arrive in render order and are drawn in it.
 */
export function useCampaignActivity(campaignId: string) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<CampaignActivityFilter>(
    CAMPAIGN_ACTIVITY_FILTERS.all,
  )
  const [page, setPage] = useState(1)

  const query = useListActivityApiV1CampaignsCampaignIdActivityGet(
    campaignId,
    { filter, page, size: PAGE_SIZE },
    { query: { placeholderData: (previous) => previous } },
  )
  const data = query.data?.data
  const counts = data?.counts

  const groups: CampaignActivityGroup[] = useMemo(
    () => (data?.groups ?? []).map(toCampaignActivityGroup),
    [data],
  )

  const chips: CampaignChip<CampaignActivityFilter>[] = useMemo(
    () =>
      CAMPAIGN_ACTIVITY_FILTER_ORDER.map((key) => ({
        key,
        label: t(campaignActivityFilterLabelKey(key)),
        count: counts?.[key] ?? 0,
        alert: CAMPAIGN_ACTIVITY_ALERT_FILTERS.includes(key),
      })),
    [t, counts],
  )

  const onFilterChange = useCallback((next: CampaignActivityFilter) => {
    setFilter(next)
    // A page number means nothing across two different feeds.
    setPage(1)
  }, [])

  /** The export takes the same filter as the feed, so it matches what is read. */
  const onExport = useCallback(() => {
    downloadCampaignCsv(
      CAMPAIGN_CSV.activity(campaignId, filter),
      `campaign-${campaignId}-activity.csv`,
    ).catch((error: unknown) =>
      toast.error(
        getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
      ),
    )
  }, [campaignId, filter, t])

  const total = data?.total ?? 0
  const pagination: DataTablePagination = {
    page,
    pageSize: PAGE_SIZE,
    totalItems: total,
    totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    onPageChange: setPage,
    onPageSizeChange: () => undefined,
  }

  return {
    groups,
    isLoading: query.isPending,
    chips,
    filter,
    onFilterChange,
    pagination,
    retention: t('campaigns.activity.retention'),
    onExport,
  }
}

export type UseCampaignActivityResult = ReturnType<typeof useCampaignActivity>
